import {
  Badge,
  FloatingTooltip,
  InputGroup,
  TooltipCore,
  ValidationMessages,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useState } from 'react';

import { SelectOptionInUse, SelectOptionInUseReason } from '@/gql/graphql';

type OptionInUse = Pick<SelectOptionInUse, 'option' | 'reasons'>;

const REASON_COPY: Record<SelectOptionInUseReason, string> = {
  SAVED_ON_RECORDS: 'saved on records',
  USED_IN_PIPELINE_STAGE_RULES: 'used in a pipeline stage rule',
};

export type Props = {
  options?: string[];
  optionsInUse?: OptionInUse[] | null;
  onChange: (options: string[]) => void;
};

const CustomFieldGroupOptionsInput: FC<Props> = (props) => {
  const { options, optionsInUse, onChange } = props;
  const inUseByOption = new Map(
    (optionsInUse ?? []).map((inUse) => [inUse.option, inUse])
  );
  const [optionToAdd, setOptionToAdd] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>();

  const handleAddOption = useCallback(() => {
    if (!optionToAdd.length) return;
    if (options?.includes(optionToAdd)) {
      setErrorMessage('This option has already been added');
      return;
    }
    onChange([...(options || []), optionToAdd]);
    setOptionToAdd('');
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onChange'
  }, [options, optionToAdd]);

  return (
    <div>
      <InputGroup sizeVariant="md">
        <InputGroup.Input
          placeholder="Type the option you want to add..."
          value={optionToAdd}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddOption();
              e.preventDefault();
            }
          }}
          onChange={(option) => {
            setOptionToAdd(option);
            setErrorMessage('');
          }}
        ></InputGroup.Input>
        <InputGroup.Button
          variant={'secondary'}
          disabled={!optionToAdd?.length}
          onClick={handleAddOption}
        >
          Add
        </InputGroup.Button>
      </InputGroup>
      {errorMessage && (
        <ValidationMessages
          validationState={{
            isValid: false,
            validationMessages: [errorMessage],
          }}
        />
      )}
      {!!options && (
        <div className="mt-2 flex flex-row flex-wrap gap-2">
          {options?.map((option, index) => {
            const inUse = inUseByOption.get(option);
            if (inUse) {
              return (
                <LockedOption
                  key={`${option}-${index}`}
                  option={option}
                  inUse={inUse}
                />
              );
            }
            return (
              <Badge
                key={`${option}-${index}`}
                rounded={false}
                size="md"
                variant="soft"
                colour="blue"
              >
                {option}

                <button
                  // Without this the button defaults to submit, so pressing
                  // Enter anywhere in the form removes the first option.
                  type="button"
                  aria-label={`Remove ${option}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(options.filter((x) => x !== option));
                  }}
                  // Padded out to a usable target, then pulled back in by the
                  // same amount so the Badge keeps its size.
                  className={`-my-1 ml-1 flex shrink-0 grow-0 cursor-pointer items-center border-none bg-transparent p-1 text-inherit outline-none transition-all`}
                >
                  <UntitledIcon name="x-close" className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomFieldGroupOptionsInput;

type LockedOptionProps = {
  option: string;
  inUse: OptionInUse;
};

const LockedOption: FC<LockedOptionProps> = (props) => {
  const { option, inUse } = props;
  // A tab opened before the server gained a new reason won't know its wording.
  const reasons = inUse.reasons.map((reason) =>
    Object.hasOwn(REASON_COPY, reason) ? REASON_COPY[reason] : 'in use'
  );
  const description = `Can't remove: ${reasons.join(' and ')}`;

  return (
    <FloatingTooltip>
      <button
        type="button"
        aria-label={`${option}. ${description}`}
        // Touch browsers don't focus a tapped button, and focus opens the tooltip.
        onClick={(e) => e.currentTarget.focus()}
        className="flex rounded-full border-none bg-transparent p-0 text-inherit"
      >
        <Badge
          rounded={false}
          size="md"
          variant="soft"
          colour="blue"
          className="opacity-50"
        >
          {option}
          {/* The lock sits inside a larger viewBox than the cross, so it's
              drawn bigger, then pulled in so the Badge keeps its size. */}
          <UntitledIcon
            name="lock-01.3"
            className="-my-0.5 ml-1 h-4 w-4 shrink-0"
          />
        </Badge>
      </button>
      <TooltipCore>{description}</TooltipCore>
    </FloatingTooltip>
  );
};
